import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import {
  AddressBook, MagnifyingGlass, EnvelopeSimple, Phone, User,
  Buildings, Truck, UserCircle, Plus, ArrowSquareOut
} from '@phosphor-icons/react';

const SOURCE_META = {
  klant:       { label: 'Klant',       bg: 'bg-green-100',  text: 'text-green-700',   icon: User,        editPath: '/klanten?edit=' },
  leverancier: { label: 'Leverancier', bg: 'bg-blue-100',   text: 'text-blue-700',    icon: Buildings,   editPath: '/leveranciers?edit=' },
  verkoper:    { label: 'Verkoper',    bg: 'bg-purple-100', text: 'text-purple-700',  icon: UserCircle,  editPath: '/instellingen?tab=verkopers' },
};

export default function ContactenPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [premiumBlocked, setPremiumBlocked] = useState(false);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/mail/contacts/all');
      setContacts(r.data || []);
      setPremiumBlocked(false);
    } catch (e) {
      if (e.response?.status === 402) setPremiumBlocked(true);
      setContacts([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return contacts.filter(c => {
      if (sourceFilter && c.source !== sourceFilter) return false;
      if (!q) return true;
      return (c.naam || '').toLowerCase().includes(q)
        || (c.email || '').toLowerCase().includes(q)
        || (c.tel || '').toLowerCase().includes(q);
    });
  }, [contacts, search, sourceFilter]);

  // Groepeer counts per bron
  const sourceCounts = useMemo(() => {
    const out = { klant: 0, leverancier: 0, verkoper: 0 };
    contacts.forEach(c => { out[c.source] = (out[c.source] || 0) + 1; });
    return out;
  }, [contacts]);

  const mailto = (email) => {
    // Open Mail Hub met prefilled to-veld via querystring
    navigate(`/mail?compose=1&to=${encodeURIComponent(email)}`);
  };

  if (premiumBlocked) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="contacten-premium">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center border-2 border-purple-200">
          <AddressBook size={48} className="mx-auto text-purple-500 mb-3" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">🔒 Adresboek is een Pro-feature</h2>
          <p className="text-gray-600 mb-4">Geconsolideerde view van al je klanten, leveranciers en verkopers — met 1-klik mailen.</p>
          <a href="/instellingen" className="btn btn-primary inline-block">Upgrade naar Pro</a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="contacten-page">
      <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <AddressBook size={24} className="text-[#8B5CF6]" weight="duotone" />
          <h1 className="text-xl font-bold text-gray-900">Adresboek</h1>
          <span className="text-xs text-gray-500">{filtered.length} van {contacts.length} contact{contacts.length === 1 ? '' : 'en'}</span>
        </div>
        <div className="flex-1 max-w-md relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Zoek op naam, e-mail of telefoon..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#002FA7]"
            data-testid="contacten-search"
          />
        </div>
        <div className="flex flex-wrap max-w-full items-center gap-1">
          <button
            onClick={() => setSourceFilter('')}
            className={`px-2.5 py-1.5 text-xs rounded border ${!sourceFilter ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#002FA7]'}`}
            data-testid="contacten-filter-all"
          >
            Alle ({contacts.length})
          </button>
          {Object.entries(SOURCE_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const active = sourceFilter === key;
            return (
              <button
                key={key}
                onClick={() => setSourceFilter(active ? '' : key)}
                className={`px-2.5 py-1.5 text-xs rounded border inline-flex items-center gap-1 ${active ? 'bg-[#002FA7] text-white border-[#002FA7]' : `${meta.bg} ${meta.text} border-gray-300 hover:border-[#002FA7]`}`}
                data-testid={`contacten-filter-${key}`}
              >
                <Icon size={11} weight="bold" />
                {meta.label} ({sourceCounts[key] || 0})
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading && contacts.length === 0 && (
          <div className="p-8 text-center text-gray-400 text-sm">⏳ Contacten laden...</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-12 text-center text-gray-400">
            <AddressBook size={48} className="mx-auto opacity-40 mb-3" />
            <div className="text-sm">{search ? `Geen contacten gevonden voor "${search}"` : 'Nog geen contacten.'}</div>
            <a href="/klanten" className="text-xs text-[#002FA7] hover:underline mt-2 inline-block">+ Voeg je eerste klant toe</a>
          </div>
        )}
        {filtered.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-[10px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Naam</th>
                <th className="px-4 py-2 text-left">E-mail</th>
                <th className="px-4 py-2 text-left">Telefoon</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-right">Acties</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => {
                const meta = SOURCE_META[c.source] || SOURCE_META.klant;
                const Icon = meta.icon;
                return (
                  <tr
                    key={c.email + i}
                    className="border-b hover:bg-gray-50 transition-colors"
                    data-testid={`contact-row-${c.email}`}
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-gray-900 truncate max-w-xs" title={c.naam}>
                        {c.naam || <span className="text-gray-400 italic">(geen naam)</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <a
                        href="#"
                        onClick={(e) => { e.preventDefault(); mailto(c.email); }}
                        className="text-[#002FA7] hover:underline text-xs inline-flex items-center gap-1"
                      >
                        <EnvelopeSimple size={11} /> {c.email}
                      </a>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-700">
                      {c.tel ? (
                        <a href={`tel:${c.tel}`} className="hover:underline inline-flex items-center gap-1 text-gray-700">
                          <Phone size={11} className="text-gray-400" /> {c.tel}
                        </a>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded ${meta.bg} ${meta.text}`}>
                        <Icon size={10} weight="bold" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => mailto(c.email)}
                          className="px-2 py-1 text-[10px] bg-[#EEF3FF] hover:bg-[#DDE7FF] text-[#002FA7] rounded inline-flex items-center gap-1"
                          title="Stuur e-mail via Mail Hub"
                          data-testid={`contact-mail-${c.email}`}
                        >
                          <EnvelopeSimple size={10} weight="bold" /> Mail
                        </button>
                        {c.id && meta.editPath && (
                          <a
                            href={`${meta.editPath}${c.id}`}
                            className="px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 text-gray-700 rounded inline-flex items-center gap-1"
                            title={`Bewerk ${meta.label.toLowerCase()}`}
                            data-testid={`contact-edit-${c.email}`}
                          >
                            <ArrowSquareOut size={10} weight="bold" /> Bewerk
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[11px] text-gray-400 px-1">
        💡 Tip: contacten komen automatisch uit Klanten, Leveranciers en Verkopers. Klik op een e-mailadres of "Mail"-knop om direct te schrijven via je gekoppelde mailbox.
      </div>
    </div>
  );
}
